// ========== Start Open & Close Compare fits popup ==========
const relatedStyleInventoryCache = new Map();

const detectRelatedStyleMarket = () => {
  const market =
    window.Shopify?.country?.toLowerCase()
    || window.Shopify?.localization?.country?.iso_code?.toLowerCase()
    || document.documentElement.getAttribute('lang')?.split('-')?.pop()?.toLowerCase();

  return market === 'us' ? 'us' : 'ca';
};

const normalizeRelatedStyleColor = (value) =>
  String(value || '')
    .trim()
    .replace(/^_+/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

const isHiddenRelatedStyleColor = (value) =>
  String(value || '').trim().startsWith('_');

const formatRelatedStyleMoney = (cents) => {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';

  if (typeof formatVariantMoney === 'function') {
    return formatVariantMoney(amount);
  }

  let formatted = typeof Shopify?.formatMoney === 'function'
    ? Shopify.formatMoney(amount).replace(/(\.|,)00\b/, '')
    : `$${amount / 100}`;
  const activeCurrency = Shopify?.currency?.active;

  if (activeCurrency === 'CAD' && !formatted.startsWith('C')) {
    formatted = `C${formatted}`;
  } else if (activeCurrency === 'AUD' && !formatted.startsWith('A')) {
    formatted = `A${formatted}`;
  }

  return formatted;
};

const parseRelatedStyleVariants = (priceContainer) => {
  const variantsScript = priceContainer?.querySelector('.js-related-style-variants');
  if (!variantsScript) return [];

  try {
    const variants = JSON.parse(variantsScript.textContent);
    return Array.isArray(variants) ? variants : [];
  } catch (error) {
    console.error('Unable to parse related style variants', error);
    return [];
  }
};

const fetchRelatedStyleSoldOutColors = async (productId, market) => {
  const cacheKey = `${productId}:${market}`;
  if (relatedStyleInventoryCache.has(cacheKey)) {
    return relatedStyleInventoryCache.get(cacheKey);
  }

  const apiBase = window.COLOR_APP_BASE || 'https://color-swatches-app-production.up.railway.app';
  const request = fetch(
    `${apiBase}/inventory?productId=${encodeURIComponent(productId)}&market=${encodeURIComponent(market)}`
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Inventory API error: ${response.status}`);
      }

      return response.json();
    })
    .then((data) => (
      Array.isArray(data?.sold_out_colors)
        ? data.sold_out_colors.map(normalizeRelatedStyleColor)
        : []
    ))
    .catch((error) => {
      relatedStyleInventoryCache.delete(cacheKey);
      throw error;
    });

  relatedStyleInventoryCache.set(cacheKey, request);
  return request;
};

const pickRelatedStyleVariant = (variants, soldOutColors) => {
  if (!variants.length) return null;

  const soldOutSet = new Set(soldOutColors);
  const visibleVariants = variants.filter((variant) => {
    if (isHiddenRelatedStyleColor(variant.option1)) return false;

    const color = normalizeRelatedStyleColor(variant.option1);
    return color && !soldOutSet.has(color);
  });

  if (!visibleVariants.length) return null;

  const orderedColors = [];
  visibleVariants.forEach((variant) => {
    if (isHiddenRelatedStyleColor(variant.option1)) return;

    const color = normalizeRelatedStyleColor(variant.option1);
    if (!color || orderedColors.includes(color)) return;
    orderedColors.push(color);
  });

  const selectedColor = orderedColors[0];
  const colorVariants = visibleVariants.filter(
    (variant) => normalizeRelatedStyleColor(variant.option1) === selectedColor
  );

  return colorVariants.find((variant) => variant.available)
    || colorVariants[0]
    || visibleVariants.find((variant) => variant.available)
    || visibleVariants[0];
};

const updateRelatedStylePriceMarkup = (priceContainer, variant) => {
  if (!priceContainer || !variant) return;

  if (typeof updateSinglePriceContainer === 'function') {
    updateSinglePriceContainer(priceContainer, variant);
    return;
  }

  const priceRoot = priceContainer.querySelector('.price');
  if (!priceRoot) return;

  const regularValue = formatRelatedStyleMoney(variant.price);
  const compareValue = Number(variant.compare_at_price) > Number(variant.price)
    ? formatRelatedStyleMoney(variant.compare_at_price)
    : '';
  const regularItem = priceRoot.querySelector('.price__regular .price-item--regular');
  const saleItem = priceRoot.querySelector('.price__sale .price-item--sale');
  const compareItem = priceRoot.querySelector('.price__sale s.price-item--regular');

  if (regularItem) regularItem.textContent = regularValue;
  if (saleItem) saleItem.textContent = regularValue;

  if (compareItem) {
    if (compareValue) {
      compareItem.textContent = compareValue;
    } else {
      const compareWrap = compareItem.closest('span');
      if (compareWrap && compareWrap.parentElement) {
        compareWrap.remove();
      } else {
        compareItem.remove();
      }
    }
  }

  priceRoot.classList.toggle(
    'price--on-sale',
    Number(variant.compare_at_price) > Number(variant.price)
  );
};

const updateRelatedStyleLinks = (root, productId, productUrl, variantId) => {
  if (!root || !productId || !productUrl || !variantId) return;

  root
    .querySelectorAll(`[data-related-style-link][data-product-id="${productId}"]`)
    .forEach((link) => {
      try {
        const url = new URL(link.dataset.productUrl || productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        link.href = `${productUrl}?variant=${variantId}`;
      }
    });
};

const syncRelatedStylePrices = async (root) => {
  const priceContainers = Array.from(root.querySelectorAll('.js-related-style-price'));
  if (!priceContainers.length) return;

  const market = detectRelatedStyleMarket();

  await Promise.all(priceContainers.map(async (priceContainer) => {
    const productId = priceContainer.dataset.productId;
    const productUrl = priceContainer.dataset.productUrl;
    const variants = parseRelatedStyleVariants(priceContainer);

    if (!productId || !variants.length) return;

    try {
      const soldOutColors = await fetchRelatedStyleSoldOutColors(productId, market);
      const selectedVariant = pickRelatedStyleVariant(variants, soldOutColors);

      if (!selectedVariant) return;

      updateRelatedStylePriceMarkup(priceContainer, selectedVariant);
      updateRelatedStyleLinks(root, productId, productUrl, selectedVariant.id);
    } catch (error) {
      console.error('Unable to sync related style pricing', error);
    }
  }));
};

const getCompareFitsButtons = (popup) => {
  const popupContainer = popup?.parentElement;
  if (!popupContainer) return [];

  return Array.from(popupContainer.querySelectorAll('.compare-fits-btn'));
};

const hasOtherStylesContent = (root) => Boolean(
  root?.querySelector('.other-styles-table__row-group')
  || root?.querySelector('.compare-fits-table__grid')
  || root?.querySelector('.compare-fits-mobile__item')
);

const hideRelatedStylesEntry = (popup) => {
  if (!popup || popup.dataset.modalMode !== 'related_styles') return;

  getCompareFitsButtons(popup).forEach((button) => {
    const relatedStylesSlot = button.closest('.related-styles-slot');

    if (relatedStylesSlot) {
      relatedStylesSlot.style.display = 'none';
      return;
    }

    button.style.display = 'none';
  });

  popup.remove();
};

// ~~~~~~~~~~ Start Helper functions ~~~~~~~~~~
const opencompareFitsModal = async (popup) => {
  if (!popup) return;

  if (popup.dataset.recommendationsFetched !== 'true') {
    const hasRecommendations = await fetchOtherStylesRecommendations(popup);
    popup.dataset.recommendationsFetched = 'true';
    popup.dataset.hasRecommendations = hasRecommendations ? 'true' : 'false';
  }

  if (popup.dataset.modalMode === 'related_styles' && popup.dataset.hasRecommendations !== 'true') {
    hideRelatedStylesEntry(popup);
    return;
  }

  popup.classList.remove('hidden');
  document.body.style.overflow = "hidden";
}

const closecompareFitsModal = (popup) => {
  if (!popup) return;
  popup.classList.add('hidden');
  document.body.style.overflow = "auto";
}
// ~~~~~~~~~~ End Helper functions ~~~~~~~~~~

const compareFitsFunction = function(popup) {
  const compareFitsbtns = getCompareFitsButtons(popup);
  const compareFitsClose = popup.querySelector('.compare-fits__close');

  if (!compareFitsbtns.length || !compareFitsClose) {
    return;
  }

  // Open Compare fits
  compareFitsbtns.forEach(btn => {
    btn.addEventListener('click', () => opencompareFitsModal(popup));
  })

  // Close Compare fits
  compareFitsClose.addEventListener('click', () => closecompareFitsModal(popup));
  popup.addEventListener('click', function(e) {if (e.target === this) closecompareFitsModal(popup)});
  window.addEventListener('keydown', e => {if (e.key === 'Escape') closecompareFitsModal(popup)});
}

const fetchOtherStylesRecommendations = async (popup) => {
  const recommendationsUrl = popup.dataset.recommendationsUrl;
  const otherStylesSection = popup.querySelector('.other-styles-section');
  const otherStylesTable = popup.querySelector('.other-styles-table');
  
  if (!recommendationsUrl || !otherStylesSection || !otherStylesTable) {
    return false;
  }

  try {
    // Fetch the compare-fits-recommendations section with product recommendations
    // Shopify will automatically populate recommendations.products when using product_recommendations_url
    let sectionUrl = recommendationsUrl + '&section_id=compare-fits-recommendations';
  
    
    const response = await fetch(sectionUrl);
    const text = await response.text();
    
    const html = document.createElement('div');
    html.innerHTML = text;
    
    // Extract the rendered fits-table from the section
    const renderedTable = html.querySelector('.other-styles-table');
    
    if (renderedTable && hasOtherStylesContent(renderedTable)) {
      otherStylesTable.innerHTML = renderedTable.innerHTML;
      await syncRelatedStylePrices(otherStylesTable);
      otherStylesSection.style.display = 'block';
      return true;
    } else {
      otherStylesTable.innerHTML = '';
      otherStylesSection.style.display = 'none';
      return false;
    }
  } catch (e) {
    otherStylesTable.innerHTML = '';
    otherStylesSection.style.display = 'none';
    console.error('Error fetching other styles recommendations:', e);
    return false;
  }
};

document.addEventListener('DOMContentLoaded', () => {
  const compareFitsPopups = Array.from(document.querySelectorAll('.compare-fits'));

  compareFitsPopups.forEach((popup) => compareFitsFunction(popup));

  compareFitsPopups
    .filter((popup) => popup.dataset.modalMode === 'related_styles')
    .forEach(async (popup) => {
      if (popup.dataset.recommendationsFetched === 'true') return;

      const hasRecommendations = await fetchOtherStylesRecommendations(popup);
      popup.dataset.recommendationsFetched = 'true';
      popup.dataset.hasRecommendations = hasRecommendations ? 'true' : 'false';

      if (!hasRecommendations) {
        hideRelatedStylesEntry(popup);
      }
    });
});
// ========== End Open & Close Compare fits popup ==========
